import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';

import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { DataSourceOrdenacaoInsensivel } from '../../../shared/helpers/ordenacao-tabela.helper';
import { RouterModule } from '@angular/router';
import { SelectionModel } from '@angular/cdk/collections';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { ConfirmModalComponent } from '../../../shared/components/confirm-modal/confirm-modal.component';
import { EmptyListComponent } from '../../../shared/components/empty-list/empty-list.component';
import { LoadingBoxComponent } from '../../../shared/components/loading-box/loading-box.component';
import { ModuloEnum } from '../../../shared/enums/usuario/modulo.enum';
import { FuncionalidadeEnum } from '../../../shared/enums/usuario/funcionalidade.enum';
import { CategoriaService } from '../../../shared/services/apis/produto/categoria.service';
import { CategoriaStruct } from '../../../shared/services/structs/produto/categoria.struct';
import { AlertService, AlertType } from '../../../shared/services/alert.service';
import { UtilService } from '../../../shared/services/util.service';
import { HeaderComponent } from '../../../shared/components/header/header.component';
import { LoadingBoxDefaultListComponent } from '../../../shared/components/loading-box-default-list/loading-box-default-list.component';

@Component({
  selector: 'app-categoria-lista',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatTableModule,
    MatCheckboxModule,
    MatMenuModule,
    MatSortModule,
    MatProgressSpinnerModule,
    MatDialogModule,
    EmptyListComponent,
    RouterModule,
    HeaderComponent,
    LoadingBoxDefaultListComponent,
  ],
  templateUrl: './categoria-lista.component.html',
  styleUrl: './categoria-lista.component.css',
})
export class CategoriaListaComponent implements OnInit, OnDestroy {
  public moduloEnum = ModuloEnum.Cadastros;
  public funcionalidadeEnum = FuncionalidadeEnum.Cadastros_Categorias;
  public model: FormGroup;
  // Pagination state
  public _paginaAtual: number = 1;
  public _tamanhoPagina: number = 20;
  public _totalRegistros: number = 0;

  public _dataSource = new DataSourceOrdenacaoInsensivel<CategoriaStruct>();
  public _isLoading: boolean = false;
  public somenteCategoriaPai: boolean = true;
  public _allData: CategoriaStruct[] = [];

  public displayedColumns: string[] = ['select', 'nome', 'arvore', 'actions'];

  public selection = new SelectionModel<CategoriaStruct>(true, []);

  @ViewChild(MatSort) set matSort(sort: MatSort) {
    this._dataSource.sort = sort;
  }

  constructor(
    private formBuilder: FormBuilder,
    private categoriaService: CategoriaService,
    private alertService: AlertService,
    private dialog: MatDialog,
    private utilService: UtilService
  ) {}

  ngOnInit(): void {
    this.model = this.formBuilder.group({
      pesquisa: [''],
    });

    this.loadFilters();
    this.carregarCategorias();
  }

  ngOnDestroy(): void {
    this.utilService.salvarFiltro(this.moduloEnum, this.funcionalidadeEnum, this.model, [
      '/cadastros/categoria/cadastrar',
    ]);
  }

  private loadFilters(): void {
    const filtroSalvo = this.utilService.obterFiltro(this.moduloEnum, this.funcionalidadeEnum);
    if (filtroSalvo) {
      this.model.patchValue(filtroSalvo);
    }
  }

  private carregarCategorias(reset: boolean = false): void {
    this._isLoading = true;
    const nome = this.model.get('pesquisa')?.value || '';

    this.categoriaService.getAll(nome, this._paginaAtual, this._tamanhoPagina).subscribe({
      next: response => {
        this._isLoading = false;
        if (response.isError) {
          this.alertService.show(
            'Erro',
            response.errorDescription || 'Erro ao carregar categorias',
            AlertType.error
          );
          return;
        }

        this._totalRegistros = response.totalRegistros;

        if (reset) {
          this._allData = response.listaCategorias;
          this.selection.clear();
        } else {
          this._allData = [...this._allData, ...response.listaCategorias];
        }
        this._dataSource.data = this.aplicarFiltroLocal(this._allData);
      },
      error: err => {
        this._isLoading = false;
        this.alertService.show(
          'Erro inesperado',
          err.message || 'Erro ao carregar categorias',
          AlertType.error
        );
      },
    });
  }

  public proximaPagina(): void {
    this._paginaAtual++;
    this.carregarCategorias();
  }

  public clickPesquisar(): void {
    this._paginaAtual = 1;
    this._allData = [];
    this._dataSource.data = [];
    this.selection.clear();
    this.carregarCategorias(true);
  }

  // Funcionalidade removida temporariamente: listar filhos fora da hierarquia deixa a edição confusa.
  // A intenção é que o cliente abra a categoria pai e edite os filhos por ela.
  // public toggleSomenteCategoriaPai(): void {
  //   this.somenteCategoriaPai = !this.somenteCategoriaPai;
  //   this._dataSource.data = this.aplicarFiltroLocal(this._allData);
  //   this.selection.clear();
  // }

  private aplicarFiltroLocal(data: CategoriaStruct[]): CategoriaStruct[] {
    if (this.somenteCategoriaPai) {
      return data.filter(c => c.idCategoriaPai == null);
    }
    return data;
  }

  public isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this._dataSource.data.length;
    return numSelected === numRows && numRows > 0;
  }

  public toggleAllRows() {
    if (this.isAllSelected()) {
      this.selection.clear();
      return;
    }
    this.selection.select(...this._dataSource.data);
  }

  public abrirModalExcluir(item?: CategoriaStruct): void {
    const isBulk = !item;
    const itemsToDelete = isBulk ? this.selection.selected : [item!];

    if (itemsToDelete.length === 0) return;

    const title = isBulk ? 'Excluir Categorias' : 'Excluir Categoria';

    let description = '';
    if (isBulk) {
      const anyWithChildren = itemsToDelete.some(i => i.possuiFilhos);
      description = `Deseja realmente excluir as ${itemsToDelete.length} categorias selecionadas?`;
      if (anyWithChildren) {
        description += ' Atenção: todas as subcategorias dos itens selecionados também serão excluídas.';
      }
    } else {
      const itemToDelete = itemsToDelete[0];
      description = itemToDelete.possuiFilhos
        ? `Deseja excluir a categoria "${itemToDelete.nome}"? Atenção: todas as suas subcategorias também serão excluídas.`
        : `Deseja realmente excluir a categoria "${itemToDelete.nome}"?`;
    }

    const dialogRef = this.dialog.open(ConfirmModalComponent, {
      data: {
        title: title,
        description: description,
        buttonConfirm: 'Excluir',
        buttonClose: 'Cancelar',
      },
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result?.confirm) {
        this._isLoading = true;

        if (isBulk) {
          const ids = itemsToDelete.map(i => i.idCategoria);
          this.categoriaService.deleteBulk(ids).subscribe({
            next: response => {
              this._isLoading = false;
              if (response.isError) {
                this.alertService.show(
                  'Atenção',
                  `Erro ao excluir itens: ${response.errorDescription}`,
                  AlertType.warning
                );
              } else {
                this.alertService.show('Sucesso', 'Categorias excluídas com sucesso', AlertType.success);
                this.selection.clear();
                this.clickPesquisar();
              }
            },
            error: err => {
              this._isLoading = false;
              this.alertService.show(
                'Erro inesperado',
                err.message || 'Erro ao excluir categorias',
                AlertType.error
              );
            },
          });
        } else {
          this.categoriaService.delete(item!.idCategoria).subscribe({
            next: response => {
              this._isLoading = false;
              if (response.isError) {
                this.alertService.show(
                  'Erro',
                  response.errorDescription || 'Erro ao excluir categoria',
                  AlertType.error
                );
              } else {
                this.alertService.show('Sucesso', 'Categoria excluída com sucesso', AlertType.success);
                this.clickPesquisar();
              }
            },
            error: err => {
              this._isLoading = false;
              this.alertService.show(
                'Erro inesperado',
                err.message || 'Erro ao excluir categoria',
                AlertType.error
              );
            },
          });
        }
      }
    });
  }
}
